"use client";

import React, { useState } from "react";

import type {
  RectificationCandidateOut,
  RectificationResponse,
} from "@/lib/api/rectification";

export interface BirthTimeRectifierProps {
  /** The engine's ranking. This component never invents candidates. */
  result: RectificationResponse;
  locale?: "en" | "hi";
  onRectifiedTimeSelect?: (candidate: RectificationCandidateOut) => void;
}

const CONFIDENCE_COPY: Record<string, { en: string; hi: string; className: string }> = {
  high: {
    en: "Strong match",
    hi: "प्रबल मिलान",
    className: "bg-success/10 text-success border-success/30",
  },
  moderate: {
    en: "Partial match",
    hi: "आंशिक मिलान",
    className: "bg-warning/10 text-warning border-warning/30",
  },
  low: {
    en: "Weak match — needs more events",
    hi: "अपर्याप्त मिलान — और घटनाएं जोड़ें",
    className: "bg-surface-pressed text-text-secondary border-border-default",
  },
};

function RashiLabel({ candidate }: { candidate: RectificationCandidateOut }) {
  return (
    <>
      <span className="rounded-md bg-surface-pressed px-2.5 py-0.5 text-xs font-medium text-text-secondary">
        {candidate.lagna_rashi} {candidate.lagna_degree.toFixed(2)}°
      </span>
      <span className="rounded-md bg-surface-pressed px-2.5 py-0.5 text-xs font-medium text-text-secondary">
        {candidate.lagna_nakshatra}-{candidate.lagna_pada}
      </span>
      <span className="rounded-md bg-surface-pressed px-2.5 py-0.5 text-xs font-medium text-text-secondary">
        D9 {candidate.d9_lagna_rashi}
      </span>
    </>
  );
}

function EventEvidence({
  candidate,
  isHi,
}: {
  candidate: RectificationCandidateOut;
  isHi: boolean;
}) {
  return (
    <ul className="mt-3 space-y-1.5 border-t border-border-subtle/80 pt-3">
      {candidate.event_matches.map((match) => (
        <li key={`${match.event_date}-${match.event_type}`} className="text-xs leading-relaxed">
          <span className="font-mono text-text-muted">{match.event_date}</span>{" "}
          <span className="font-medium text-text-primary">
            {match.event_type.replace(/_/g, " ")}
          </span>
          <span className="text-text-muted">
            {" "}
            · {match.mahadasha_lord}/{match.antardasha_lord}
          </span>
          <span className={match.matched ? " text-success" : " text-text-muted"}>
            {" "}
            {match.matched ? (isHi ? "✓ राशि" : "✓ D1") : isHi ? "— राशि" : "— D1"}
          </span>
          <span className={match.navamsha_matched ? " text-success" : " text-text-muted"}>
            {match.navamsha_matched ? (isHi ? " ✓ नवमांश" : " ✓ D9") : isHi ? " — नवमांश" : " — D9"}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function BirthTimeRectifier({
  result,
  locale = "en",
  onRectifiedTimeSelect,
}: BirthTimeRectifierProps) {
  const isHi = locale === "hi";
  const [selectedTime, setSelectedTime] = useState<string>(result.candidates[0]?.birth_time ?? "");
  const confidence = CONFIDENCE_COPY[result.confidence] ?? CONFIDENCE_COPY.low!;

  const handleSelect = (candidate: RectificationCandidateOut) => {
    setSelectedTime(candidate.birth_time);
    onRectifiedTimeSelect?.(candidate);
  };

  if (result.candidates.length === 0) return null;

  return (
    <div
      className="w-full rounded-2xl border border-border-default bg-surface-raised p-5 shadow-card transition-all sm:p-6"
      aria-label={isHi ? "जन्म समय परिशोधन परिणाम" : "Birth time rectification candidates"}
    >
      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xl">⏱️</span>
            <h3 className="text-base font-semibold tracking-tight text-text-primary sm:text-lg">
              {isHi ? "संभावित जन्म समय" : "Candidate Birth Times"}
            </h3>
          </div>
          <p className="mt-0.5 text-xs text-text-secondary sm:text-sm">
            {isHi
              ? `${result.candidates.length} लग्न-पाद, आपकी घटनाओं से विंशोत्तरी मिलान द्वारा क्रमबद्ध`
              : `${result.candidates.length} Lagna padas, ranked by Vimshottari agreement with your events`}
          </p>
        </div>

        <span
          className={`inline-flex items-center gap-1.5 self-start rounded-full border px-3 py-1 text-xs font-semibold sm:self-auto ${confidence.className}`}
        >
          {isHi ? confidence.hi : confidence.en}
        </span>
      </div>

      <div className="my-4 space-y-3">
        {result.candidates.map((candidate) => {
          const isSelected = candidate.birth_time === selectedTime;
          return (
            <button
              key={candidate.birth_time}
              type="button"
              onClick={() => handleSelect(candidate)}
              aria-pressed={isSelected}
              className={`w-full rounded-xl border p-4 text-left transition-all ${
                isSelected
                  ? "border-border-interactive bg-surface-brand-subtle shadow-xs ring-2 ring-intent-brand"
                  : "border-border-subtle bg-surface-raised hover:border-border-default"
              }`}
            >
              <div className="mb-2 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-mono text-base font-bold text-text-primary sm:text-lg">
                    {candidate.birth_time}
                  </span>
                  <RashiLabel candidate={candidate} />
                </div>

                <span className="font-mono text-xs font-bold text-text-secondary">
                  {candidate.matched_events + candidate.navamsha_matched_events}/
                  {candidate.total_events * 2} {isHi ? "प्रमाण" : "arguments"}
                </span>
              </div>

              <EventEvidence candidate={candidate} isHi={isHi} />
            </button>
          );
        })}
      </div>

      {/* The boundary is not decoration — a ranking shown as a determination is
          the failure this engine replaced (rule 29). */}
      <div className="rounded-xl border border-border-subtle bg-surface-canvas p-3.5 text-xs">
        <p className="leading-relaxed text-text-secondary">{result.boundary}</p>
      </div>
    </div>
  );
}
